/* Combine ordinary 10 x 500g packs without touching personalised box lines. */
(() => {
  const root = () => window.Shopify?.routes?.root || '/';
  const signature = properties => JSON.stringify(Object.entries(properties || {}).filter(([, v]) => v !== '').sort(([a], [b]) => a.localeCompare(b)));
  const ordinary = item => !item.selling_plan_allocation && !item.parent_relationship && Object.keys(item.properties || {}).every(key => key === '_Delivery Route');
  const packSize = title => /^10X500G(?:PACKS?)?$/.test(String(title).toUpperCase().replace(/[\s×]/g, ch => ch === '×' ? 'X' : ''));
  let queue = Promise.resolve();
  let busy = 0;

  async function request(path, body) {
    const response = await fetch(root() + path, body ? {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)} : {headers: {Accept: 'application/json'}, cache: 'no-store'});
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      const error = new Error(data.description || data.message || 'Unable to update your bowl.');
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async function normalize(cart, sections) {
    cart = cart || await request('cart.js');
    const groups = new Map();
    for (const item of cart.items) {
      if (!ordinary(item) || !packSize(item.variant_title)) continue;
      const key = item.product_id;
      const group = groups.get(key) || [];
      group.push(item);
      groups.set(key, group);
    }
    let converted = null;
    for (const group of groups.values()) {
      const count = group.reduce((sum, item) => sum + item.quantity, 0);
      if (count < 2) continue;
      const routes = new Set(group.map(item => item.properties?.['_Delivery Route']).filter(Boolean));
      if (routes.size > 1) continue;
      const first = group.find(item => item.properties?.['_Delivery Route']) || group[0];
      let product;
      try { product = await request('products/' + encodeURIComponent(first.handle) + '.js'); }
      catch (_) { continue; }
      const large = product.variants.find(variant => /^20X500G(?:PACKS?)?$/.test(variant.title.toUpperCase().replace(/[\s×]/g, ch => ch === '×' ? 'X' : '')));
      if (!large?.available) continue;
      const pairs = Math.floor(count / 2);
      const properties = first.properties || {};
      const matching = item => Number(item.variant_id) === Number(large.id) && ordinary(item) && signature(item.properties) === signature(properties);
      const beforeLarge = cart.items.filter(matching).reduce((sum, item) => sum + item.quantity, 0);
      let added;
      try {
        added = await request('cart/add.js', {items: [{id: large.id, quantity: pairs, properties}]});
      } catch (error) {
        // Shopify validates inventory on add, including an existing 20-pack line.
        // A rejected or interrupted add can still have changed inventory in the cart.
        const current = await request('cart.js');
        const rollback = {};
        let keep = beforeLarge;
        current.items.filter(matching).forEach(item => { rollback[item.key] = Math.min(keep, item.quantity); keep -= rollback[item.key]; });
        if (current.items.filter(matching).reduce((sum, item) => sum + item.quantity, 0) > beforeLarge) {
          cart = await request('cart/update.js', {updates: rollback});
        }
        if (error.status === 422) continue;
        throw error;
      }
      const isSmall = item => Number(item.variant_id) === Number(first.variant_id) && ordinary(item);
      const smallCount = state => state.items.filter(isSmall).reduce((sum, item) => sum + item.quantity, 0);
      try {
        // Adding the larger variant or delivery updates can change line keys.
        // Always remove using fresh keys and verify, even after an HTTP 200.
        cart = await request('cart.js');
        for (let attempt = 0; attempt < 2 && smallCount(cart) !== count % 2; attempt++) {
          if (smallCount(cart) !== count) throw new Error('Your bowl changed during pack conversion.');
          const updates = {};
          let remainder = count % 2;
          cart.items.filter(isSmall).forEach(item => {
            updates[item.key] = Math.min(remainder, item.quantity);
            remainder -= updates[item.key];
          });
          await request('cart/update.js', {updates, sections, sections_url: window.location.pathname});
          cart = await request('cart.js');
        }
        if (smallCount(cart) !== count % 2) throw new Error('Unable to remove the smaller packs.');
      } catch (error) {
        const current = await request('cart.js');
        if (smallCount(current) !== count % 2) {
          const rollback = {};
          let keep = beforeLarge;
          current.items.filter(matching).forEach(item => { rollback[item.key] = Math.min(keep, item.quantity); keep -= rollback[item.key]; });
          await request('cart/update.js', {updates: rollback});
          throw error;
        }
        cart = current;
      }
      converted = cart.items.find(matching) || added.items[0];
    }
    if (!converted) return null;
    // Re-render from the final cart so prices, bowl counts and notices agree.
    if (sections?.length) {
      const rendered = await request('?sections=' + encodeURIComponent(sections.join(',')));
      cart.sections = rendered;
    }
    return {cart, item: converted};
  }

  function run(task) {
    busy++;
    const result = queue.catch(() => {}).then(task);
    queue = result.catch(() => {});
    return result.finally(() => { busy--; });
  }

  function review() {
    try { sessionStorage.setItem('trd-pack-review', 'Your items were added, but we couldn’t finish combining the packs. Please review your bowl before checkout.'); } catch (_) {}
    window.location.assign(window.routes.cart_url);
  }

  window.TRDPackConversion = {normalize, run, review, whenIdle: () => queue, get busy() { return busy > 0; }};
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (busy && link && !link.href.includes('/checkout') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && link.target !== '_blank') {
      event.preventDefault(); event.stopImmediatePropagation();
      queue.then(() => window.location.assign(link.href));
      return;
    }
    if (busy && event.target.closest('[name="checkout"],a[href*="/checkout"],.shopify-payment-button')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => {
    if (busy && event.submitter?.name === 'checkout') { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
    try {
      const message = sessionStorage.getItem('trd-pack-review');
      if (message) {
        sessionStorage.removeItem('trd-pack-review');
        const notice = document.createElement('p'); notice.setAttribute('role', 'alert'); notice.textContent = message;
        document.querySelector('#MainContent')?.prepend(notice);
      }
    } catch (_) {}
  }
})();
